import { getMetrics } from '@/pages/intelligence/services/sourceMetrics';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Justify, Button, SearchBox } from 'tea-component';
import { useHistory, useIntl, getLocale } from 'umi';
import SourceTable from '../../../Sources/component/Table';
import TextMore from '../../../Sources/component/TextMore';
import ChannelLabel from '../../../Sources/component/ChannelLabel';
import DataPreviewLink from '../../../Sources/component/DataPreviewLink';
import Filter from '../../../Sources/component/Filter/MetricsFilter';
import { gameTypeCN } from '../../../Sources/component/OpinionsSources';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { uniq } from 'lodash-es';
import { buriedPointPage, buriedPointSearch } from '@/pages/home/utils/buriedPoint';
import { useSearchParamsState } from '@/pages/intelligence/pages/GlobalView/MarketSize/hooks/useQuery';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import {
  buildDataSourceFilterOptionsFromTabResponse,
  getRecordIconSrc,
  getUrlBySelfs,
  resolveLegacyPageId,
  type TDataSourceFilterOption,
} from '../../../utils';
import { filterMetricsRecordsBySearch } from '../../../searchUtils';
import { dataSourceAllMetric } from '@/pages/intelligence/components/SourceMetricsSelector/MetricsAllConst';

type ObjectString<S = string> = { [key in string]: S };

// 目前 PC 页未加 wishlist 指标，指定跳转 pc&console 页，如 pc 添加后可直接使用后端返回的 url
const wishListMetric = ['Daily Wishlist', 'Lifetime Wishlist'];
const wishListJumpCombineUrl = '/intelligence/topCharts/pcConsoleGames';

const MAPCONST: { [key in string]: string } = {
  DAUL: 'Calibrated DAU',
  revenueL: 'Calibrated Revenue',
  downloadL: 'Calibrated Downloads',
  time_spent_0H: 'Time Spent 0 - 1 hrs',
  time_spent_1H: 'Time Spent 1 - 5 hrs',
  time_spent_5H: 'Time Spent 5 - 10 hrs',
  time_spent_10H: 'Time Spent 10 - 25 hrs',
  time_spent_25H: 'Time Spent 25 - 50 hrs',
  time_spent_50H: 'Time Spent >50 hrs',
  retention_d1: 'Retention D1',
  retention_d2: 'Retention D2',
  retention_d4: 'Retention D4',
  retention_d7: 'Retention D7',
  retention_d14: 'Retention D14',
  retention_d28: 'Retention D28',
};
// 针对旧风格进行路径映射
export const splitOldRouterPathNameList = <S extends string>(url: S): string => {
  if (!url || typeof url !== 'string') {
    return '';
  }
  // 榜单一级路径
  const monthlyFocusPathName: ObjectString = {
    TopCharts: 'topCharts',
    Streaming: 'streaming',
    StoreRanks: 'storeRanks',
  };
  // 榜单下面的二级路径，也就是左侧tab栏
  const monthlyFocusTwoPathName: ObjectString = {
    // TopCharts
    MobileGames: 'mobileGames',
    CompanyNew: 'companies',
    PcGames: 'pcGames',
    ConsoleGames: 'consoleGames',
    // Streaming
    Games: 'games',
    KOL: 'kol',
    // StoreRanks
    MobileGlobalRegion: 'MobileGlobalRegion',
    PCGlobal: 'PCGlobal',
    ConsoleRegion: 'ConsoleRegion',
    pcConsoleGames: 'pcConsoleGames',
  };
  // 榜单下面的三级路径，也就是顶部tab栏,因为每个榜单的id不一，这里取与接口不一致的，如果一致，取接口本身的
  const monthlyFocusThreeathName: ObjectString = {
    DAU: 'ActiveUsers',
    Revenue: 'PhysicalRevenue',
    Steam: 'steam',
    EpicGames: 'epic',
    kol: 'KOL',
    games: 'Games',
  };
  const [path1, path2, path3, path4, path5] = url.split('/');
  // 有2个榜单有相同的Revenue字段，需要区分一下
  const editPath5 =
    path4 === 'MobileGames' && path5 === 'Revenue' ? 'Revenue' : monthlyFocusThreeathName[path5];
  const res = `/intelligence/${monthlyFocusPathName[path3] || path3}/${
    monthlyFocusTwoPathName[path4]
  }/${editPath5 ?? (path5 || '')}`;
  return res;
};

const getPageIdFromUrl = (queryPageId: string) => {
  if (!queryPageId) {
    return '';
  }
  return decodeURIComponent(queryPageId);
};
const databarinMetricKey = (metric: string) => MAPCONST[metric] || metric;
export const getMetricKey = (metric: string, source: string) => {
  if (source === 'databarin' || source === 'ogdb') {
    return databarinMetricKey(metric);
  }
  if (metric === 'revenue' && source === 'npd') {
    return 'physical_revenue';
  }
  if (metric === 'units' && source === 'npd') {
    return 'physical_units';
  }
  if (source === 'famitsu' && metric !== 'sales') {
    return '';
  }
  return metric;
};
const getMetricList = (urlMetrics: string, source = '') => {
  const queryMetric = decodeURIComponent(urlMetrics);
  if (!queryMetric) {
    return [];
  }
  const metricList = queryMetric?.split(',') || [];
  if (Array.isArray(metricList) && metricList?.length === 1) {
    const metric = getMetricKey(metricList[0], source);
    const metricFilterList = dataSourceAllMetric.filter(
      (item) => item?.keyValue?.includes(metric) || metric === item?.value,
    );
    return metricFilterList.map((item) => item?.value || '');
  }
  return uniq(metricList?.filter(Boolean));
};
const GAMETYPEMAP = [
  {
    key: 'mobile',
    value: 'Mobile',
  },
  {
    key: 'pc',
    value: 'PC',
  },
  {
    key: 'console',
    value: 'Console',
  },
  {
    key: 'combine',
    value: 'combine',
  },
];
const getGameType = (ulrGameType) => {
  if (!ulrGameType) {
    return [];
  }
  const gameTypeList = ulrGameType?.split(',') || [];
  if (Array.isArray(gameTypeList)) {
    const res = gameTypeList?.map((item) => {
      const mapObj = GAMETYPEMAP.find((i) => i.key === item?.toLowerCase());
      return mapObj?.value;
    });

    return res?.[0] === 'combine' ? ['PC', 'Console'] : uniq(res?.filter(Boolean));
  }
  return [];
};

export default function IntelligenceMetrics(props: any) {
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [dataCN, setDataCN] = useState([]);
  const [dataEN, setDataEN] = useState([]);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const debounce = useRef({ interval: 0, delay: 500 }).current;

  const [searchValue, setSearchValue] = useState('');
  const [searchShow, setSearchShow] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);

  const [showCN, setShowCN] = useState([]);
  const [showEN, setShowEN] = useState([]);
  const [dataSourceOptions, setDataSourceOptions] = useState<TDataSourceFilterOption[]>([]);
  const metricQuery = useSearchParamsState('metric', '')?.[0];
  const pageIdQuery = useSearchParamsState('pageid', '')?.[0];
  const pageid = getPageIdFromUrl(pageIdQuery);
  const resolvedSourceFromUrl = useMemo(
    () =>
      pageid
        ? pageid.split(',').map(resolveLegacyPageId).filter(Boolean)
        : [],
    [pageid],
  );
  const metricUrlList = getMetricList(metricQuery, pageIdQuery);
  const gameTypeList = getGameType(useSearchParamsState('gameType', '')?.[0]);

  const history = useHistory();
  const pageLogMes = dbiPageLogMes.sourcesMetricsMetricsIntelligenceMetrics;

  const getData = (params?: any) => {
    setLoading(true);

    const param = {
      tab: 'intelligence',
      ...params,
    };

    if (!isEmpty(param) && !isEmpty(param?.tab)) {
      getMetrics(param)
        .then((data) => {
          setDataCN([]);
          setDataEN([]);

          data?.forEach((item) => {
            const tmp =
              item.list.map((item, idx) => ({
                ...item,
                url: splitOldRouterPathNameList(item.url),
                id: idx + 1,
              })) || [];

            if (item.language === 'cn') {
              setDataCN(tmp);
            } else {
              setDataEN(tmp);
            }
          });
        })
        .catch((err) => {
          console.log(err);
          setIsError(true);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setDataCN([]);
      setDataEN([]);
      setLoading(false);
    }
  };

  const data = useMemo(
    () => (locale == 'zh-CN' && dataCN.length > 0 ? dataCN : dataEN),
    [locale, dataCN, dataEN],
  );
  // const handleOnClickOpenMonthlyFocus = (
  //   value: LocationDescriptor<unknown>,
  // ) => {
  //   history.push(value);
  // };

  const columns = useMemo(
    () => [
      {
        key: 'id',
        fixed: 'left',
        align: 'left',
        width: 70,
        header: '#',
      },
      {
        key: 'metric',
        fixed: 'left',
        align: 'left',
        width: 250,
        header: formatMessage({ id: 'common_metrics' }),
        render: (cvm) => (
          <div
            className={'dbi-channel-label-point'}
            onClick={() => {
              const isWishlist = wishListMetric.includes(cvm.metric);
              if (isWishlist) {
                history.push(wishListJumpCombineUrl);
                return;
              }

              const url = cvm.url ? cvm.url : getUrlBySelfs(cvm?.data_source);
              history.push(url);
            }}
            // onClick={() => handleOnClickOpenMonthlyFocus(cvm.url)}
          >
            {cvm?.metric ? cvm?.metric : '-'}
          </div>
        ),
      },
      {
        key: 'data_source',
        align: 'left',
        width: 200,
        header: formatMessage({ id: 'Data Source' }),
        render: (record: any) => {
          const replaceText = record.data_source && record.data_source === 'DataBrain' ? 'No-Update' : record.data_source;

          return <ChannelLabel
            iconUrl={getRecordIconSrc(record)}
            channel_name={record.data_source.toLowerCase()}
            // replaceText={record.data_source}
            replaceText={replaceText}
          />;
        },
      },
      {
        key: 'metric_definition',
        align: 'left',
        width: 480,
        header: formatMessage({ id: 'dbi-sm-table-definition' }),
        render: (cvm) =>
          cvm?.metric_definition ? (
            <TextMore
              data={cvm?.metric_definition.replace(/(\r\n|\n|\r)/gm, '<br/>')}
              title={formatMessage({ id: 'dbi-sm-table-definition' })}
            />
          ) : (
            '-'
          ),
      },
      {
        key: 'game_type',
        align: 'left',
        width: 180,
        header: formatMessage({ id: 'dbi-sm-filter-gametype' }),
        render: (cvm) =>
          cvm?.game_type ? (locale == 'zh-CN' ? gameTypeCN(cvm?.game_type) : cvm?.game_type) : '-', // 后端接口没有返回中文，定义方法进行翻译
      },
      {
        key: 'granularity',
        align: 'left',
        header: formatMessage({ id: 'dbi-sm-filter-granularity' }),
        width: 150,
        render: (cvm) => (cvm?.granularity ? cvm?.granularity : '-'),
      },
      {
        key: 'cover_country',
        align: 'left',
        width: 480,
        header: formatMessage({ id: 'dbi-sm-table-countries' }),
        render: (cvm) =>
          cvm?.cover_country ? (
            <TextMore
              data={cvm?.cover_country.replace(/(\r\n|\n|\r)/gm, '<br/>')}
              title={formatMessage({ id: 'dbi-sm-table-countries' })}
            />
          ) : (
            '-'
          ),
      },
      {
        key: 'history_data',
        align: 'left',
        header: formatMessage({ id: 'dbi-sm-table-historical' }),
        width: 180,
        render: (cvm) => (cvm?.history_data ? cvm?.history_data : '-'),
      },
      {
        key: 'frequency',
        align: 'left',
        header: formatMessage({ id: 'dbi-sm-filter-frequency' }),
        width: 200,
        render: (cvm) =>
          cvm?.frequency ? (
            <TextMore
              data={cvm?.frequency.replace(/(\r\n|\n|\r)/gm, '<br/>')}
              title={formatMessage({ id: 'dbi-sm-filter-frequency' })}
            />
          ) : (
            '-'
          ),
      },
      {
        key: 'update',
        align: 'left',
        header: formatMessage({ id: 'dbi-sm-table-dateupdate' }),
        width: 250,
        render: (cvm) =>
          cvm?.update ? (
            <TextMore
              data={cvm?.update.replace(/(\r\n|\n|\r)/gm, '<br/>')}
              title={formatMessage({ id: 'dbi-sm-table-dateupdate' })}
            />
          ) : (
            '-'
          ),
      },
      {
        key: 'url',
        align: 'left',
        width: 120,
        header: formatMessage({ id: 'dbi-sm-table-actions' }),
        render: (cvm) => {
          const isWishlist = wishListMetric.includes(cvm.metric);

          if (cvm?.url || isWishlist) {
            return <DataPreviewLink url={isWishlist ? wishListJumpCombineUrl : cvm?.url} />;
          }

          if (getUrlBySelfs(cvm?.data_source)) {
            return (
              <Button type="link" onClick={() => history.push(getUrlBySelfs(cvm?.data_source))}>
                {formatMessage({ id: 'dbi_data_preview' })}
              </Button>
            );
          }

          return <>-</>;
        },
      },
    ],
    [locale],
  );

  const buildInitialFilterParams = useCallback(() => {
    const params: Record<string, unknown> = {};
    if (resolvedSourceFromUrl.length) {
      params.source = resolvedSourceFromUrl;
    }
    if (metricUrlList.length) {
      params.metrics = metricUrlList;
    }
    if (gameTypeList.length) {
      params.game_type = gameTypeList;
    }
    return params;
  }, [resolvedSourceFromUrl, metricUrlList, gameTypeList]);

  useEffect(() => {
    getMetrics({ tab: 'intelligence' })
      .then(data => {
        const options = buildDataSourceFilterOptionsFromTabResponse(data);
        if (options.length > 0) {
          setDataSourceOptions(options);
        }
      })
      .catch(err => {
        console.debug('fetch data source filter options failed', err);
      });
  }, []);

  // 当外部传进一个参数的时候，也就是路由变化的时候这个页面将会重新report
  useEffect(() => {
    getData(buildInitialFilterParams());
    buriedPointPage(pageLogMes);
  }, []);

  const handleConfirm = async (value: any) => {
    // console.log("CONFIRM", value)
    getData(value);
    setSearchValue('');
    setSearchShow(false);
    setPageIndex(1);
  };

  const handleReset = () => {
    // console.log("RESET")
    setSearchValue('');
    setSearchShow(false);
  };

  /**
   * @description: 设置debounce，避免搜索频繁更改Report列表
   * @param {string} word 搜索关键字
   * @return {*}
   */
  const debounceSearchChange = (word: string) => {
    clearTimeout(debounce.interval);
    // @ts-ignore
    debounce.interval = setTimeout(() => {
      handleSearch(word);
      if (pageLogMes?.pageId) {
        buriedPointSearch({ extInfo: pageLogMes?.pageId, typeId: pageLogMes?.typeId }, word);
      }
    }, debounce.delay);
  };

  const handleSearch = (word: string) => {
    if (word == '') {
      setSearchShow(false);
    } else {
      const showEN = filterMetricsRecordsBySearch(dataEN, word);
      const showCN = filterMetricsRecordsBySearch(dataCN, word);
      setShowCN(showCN);
      setShowEN(showEN);
      setSearchShow(true);
      setPageIndex(1);
    }
  };

  const show = useMemo(
    () => (locale == 'zh-CN' && showCN.length > 0 ? showCN : showEN),
    [locale, showCN, showEN],
  );

  return (
    <>
      <Filter
        filterConfirm={handleConfirm}
        filterReset={handleReset}
        pageid={pageid}
        metricValue={metricUrlList}
        gameTypeList={gameTypeList}
        dataSourceOptions={dataSourceOptions}
      />
      <Justify
        className="dbi-imurreports-justify"
        style={{ marginTop: '20px' }}
        right={
          <SearchBox
            className="edit-search-box"
            placeholder={formatMessage({ id: 'dbi_search_for_metrics' })}
            value={searchValue}
            onSearch={() => {
              // debounceSearchChange();
            }}
            onChange={(word) => {
              setSearchValue(word);
              if (word == '') {
                setSearchShow(false);
              } else if (word.replace(/^\s*/g, '')) {
                debounceSearchChange(word);
              }
            }}
            onClear={() => {
              setSearchShow(false);
            }}
          />
        }
      />
      <SourceTable
        records={searchShow ? show : data}
        columns={columns}
        loading={loading}
        error={isError}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
      />
    </>
  );
}
